import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { BRAND_NAME } from "Constant/constants";
import useAuth from "Context/useAuth";

export default function DataSecurity() {
  const { toggleContactSalesModal } = useAuth();
  const { t } = useTranslation();

  // SEO Setup
  const baseUrl = process.env.REACT_APP_DEV_URL;
  const pageTitle = t("common.seo.dataSecurity.pageTitle", {
    brandName: BRAND_NAME,
  });
  const canonicalUrl = `${baseUrl}/data-security`;
  const ogImageUrl = `${baseUrl}/assets/images/home/data-security-pattern.jpg`;

  useEffect(() => {
    window.scrollTo(0, 0, {
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={t("common.seo.dataSecurity.metaDescription", {
            brandName: BRAND_NAME,
          })}
        />

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={t("common.seo.dataSecurity.ogTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          property="og:description"
          content={t("common.seo.dataSecurity.ogDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={t("common.seo.dataSecurity.twitterTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          name="twitter:description"
          content={t("common.seo.dataSecurity.twitterDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <section className="position-relative">
        <div className="pt-10 position-relative z-1">
          <div className="container-xl d-flex flex-column row-gap-5">
            <div className="rounded-2xl bg-primary-900 text-white position-relative overflow-hidden">
              <div className="position-absolute top-0 start-0 bottom-0 end-0 data-security-oberlay">
                <img
                  src="assets/images/home/data-security-pattern.jpg"
                  alt=""
                  className="w-100 h-100 object-fit-cover"
                />
              </div>
              <div className="py-lg-20 py-10 px-5 row justify-content-center position-relative">
                <div className="col-xxl-6 col-xl-9 col-lg-12 col-md-12 col-sm-12 col-12">
                  <div className="text-center">
                    <h2 className="text-xxl-12xl text-xl-10xl text-lg-8xl text-md-5xl text-2xl font-bold">
                      Your Data, Our Commitment to Safety
                    </h2>
                    <p className="text-xl font-medium">
                      Learn More About Our Security Practices below.
                    </p>
                    <button
                      className="mt-5 px-8 py-3 btn btn-light text-xl-lg font-medium  d-inline-flex align-items-center gap-1"
                      onClick={toggleContactSalesModal}
                    >
                      Unlock 6 Months Free Trial
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-lg-20 py-10">
        <div className="container-xl">
          <div className="row align-items-center gx-xl-5 gy-4">
            <div className="col-lg-6 col-md-6 col-sm-12 col-12 order-md-last">
              <img
                src="assets/images/home/robust-global-data-storage.png"
                alt=""
                className="img-fluid"
              />
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 col-12 order-md-first">
              <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-2xl font-bold">
                Robust & Global Data Storage
              </h2>
              <p>
                At PlutoEdu, your data is hosted on powerful, globally
                distributed platforms to ensure high availability and
                performance. We utilize top-tier cloud services to keep your
                information safe, secure, and always accessible.
              </p>

              <ul className="list-unstyle m-0 p-0 d-flex flex-column row-gap-4 mb-8">
                <li className="d-flex align-items-center gap-3">
                  <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                    >
                      <path
                        d="M21 11.4995C21 12.8128 20.7414 14.1132 20.2388 15.3265C19.7362 16.5399 18.9996 17.6423 18.0709 18.5709C17.1423 19.4996 16.0399 20.2362 14.8265 20.7388C13.6132 21.2414 12.3128 21.5 10.9995 21.5C9.68624 21.5 8.38581 21.2414 7.1725 20.7388C5.95918 20.2362 4.85673 19.4996 3.9281 18.5709C2.99947 17.6423 2.26284 16.5399 1.76027 15.3265C1.25769 14.1132 0.999023 12.8128 0.999023 11.4995C0.999023 8.84723 2.05264 6.30356 3.9281 4.4281C5.80356 2.55264 8.34723 1.49902 10.9995 1.49902C13.6518 1.49902 16.1955 2.55264 18.0709 4.4281C19.9464 6.30356 21 8.84723 21 11.4995Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M15.0001 11.5C15.0001 12.813 14.8961 14.114 14.6951 15.327C14.4951 16.54 14.2001 17.642 13.8281 18.571C13.4571 19.5 13.0161 20.236 12.5311 20.739C12.0451 21.241 11.5251 21.5 11.0001 21.5C10.4751 21.5 9.95506 21.241 9.47006 20.739C8.98406 20.236 8.54305 19.499 8.17205 18.571C7.80005 17.642 7.50505 16.541 7.30405 15.327C7.09896 14.0617 6.99729 12.7818 7.00005 11.5C7.00005 10.187 7.10305 8.886 7.30405 7.673C7.50505 6.46 7.80005 5.358 8.17205 4.429C8.54305 3.5 8.98405 2.764 9.46905 2.261C9.95505 1.76 10.4751 1.5 11.0001 1.5C11.5251 1.5 12.0451 1.759 12.5301 2.261C13.0161 2.764 13.4571 3.501 13.8281 4.429C14.2001 5.358 14.4951 6.459 14.6951 7.673C14.8971 8.886 15.0001 10.187 15.0001 11.5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M1 11.5H21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="mb-0">
                    <strong>Global Reach:</strong> Our platform leverages AWS
                    and Google Cloud to ensure high availability and low latency
                    worldwide.
                  </p>
                </li>
                <li className="d-flex align-items-center gap-3">
                  <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="21"
                      viewBox="0 0 20 21"
                      fill="none"
                    >
                      <path
                        d="M10.0001 10.0422C9.42432 10.0422 8.87211 9.81347 8.46497 9.40632C8.05783 8.99918 7.8291 8.44698 7.8291 7.8712C7.8291 7.29541 8.05783 6.74321 8.46497 6.33607C8.87211 5.92893 9.42432 5.7002 10.0001 5.7002C10.576 5.7002 11.1283 5.92898 11.5356 6.33621C11.9428 6.74345 12.1716 7.29578 12.1716 7.8717C12.1716 8.44761 11.9428 8.99994 11.5356 9.40718C11.1283 9.81441 10.576 10.0432 10.0001 10.0432V13.2992"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.6721 10.3898V5.10983C18.6721 4.70471 18.5446 4.30987 18.3077 3.98124C18.0708 3.65262 17.7365 3.40687 17.3521 3.27883L12.4381 1.63983C10.8546 1.11875 9.14569 1.11875 7.56213 1.63983L2.64812 3.27783C2.26393 3.40582 1.92973 3.65141 1.69284 3.97984C1.45594 4.30826 1.32835 4.70288 1.32812 5.10783V10.3878C1.32831 11.6912 1.65889 12.9732 2.28898 14.1141C2.91906 15.255 3.82809 16.2176 4.93113 16.9118L8.97913 19.4558C9.28544 19.6468 9.63917 19.748 10.0001 19.748C10.3611 19.748 10.7148 19.6468 11.0211 19.4558L15.0681 16.9118C16.1712 16.2178 17.0803 15.2554 17.7106 14.1147C18.3409 12.974 18.6717 11.6921 18.6721 10.3888"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="mb-0">
                    <strong>Encrypted Protection:</strong> All data is secured
                    with state-of-the-art encryption, both in transit and at
                    rest, safeguarding against unauthorized access.
                  </p>
                </li>
              </ul>
              <div>
                <a
                  href="#"
                  className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1"
                >
                  Join PlutoEdu
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-lg-20 py-10 bg-light">
        <div className="container-xl d-flex flex-column row-gap-4">
          <div className="row justify-content-center align-items-center gx-xl-5">
            <div className="col-xl-8 col-lg-10 col-md-12 col-sm-12 col-12 text-center">
              <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-2xl font-bold">
                Built on Trust and Compliance
              </h2>
              <p>
                Pluto Edu adheres to stringent global standards, ensuring that
                your data remains secure and compliant with the latest
                regulations.
              </p>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3 bg-white">
                <div className="rounded-md overflow-hidden">
                  <img
                    src="assets/images/home/gdpr.jpg"
                    alt=""
                    className="w-100"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-grey-800">GDPR</h3>
                  <p>
                    We meet or exceed these benchmarks for data privacy and
                    security.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3 bg-white">
                <div className="rounded-md overflow-hidden">
                  <img
                    src="assets/images/home/institutional-policies.jpg"
                    alt=""
                    className="w-100"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-grey-800">
                    Institutional Policies
                  </h3>
                  <p>
                    Our system aligns with local university security protocols
                    to safeguard sensitive information.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3 bg-white">
                <div className="rounded-md overflow-hidden">
                  <img
                    src="assets/images/home/continuous-monitoring.jpg"
                    alt=""
                    className="w-100"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-grey-800">
                    Continuous Monitoring
                  </h3>
                  <p>
                    Our security team conducts regular audits and continuous
                    monitoring to proactively identify and mitigate risk
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-lg-20 py-10">
        <div className="container-xl d-flex flex-column row-gap-4">
          <div className="row justify-content-center align-items-center gx-xl-5">
            <div className="col-xl-8 col-lg-10 col-md-12 col-sm-12 col-12 text-center">
              <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-2xl font-bold">
                Granular Access & Thoughtful Data Retention
              </h2>
            </div>
          </div>

          <div className="row justify-content-center mb-5">
            <div className="col-xl-5 col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="h-100 p-5 rounded-xl border border-grey-200 d-flex flex-column row-gap-3 bg-grey-50 text-center">
                <div className="rounded-md overflow-hidden">
                  <div className="flex-auto w-12 h-12 mx-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="22"
                      viewBox="0 0 35 28"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.4444 5.05566C11.8826 5.05566 11.3263 5.16631 10.8073 5.38129C10.2883 5.59627 9.81676 5.91137 9.41953 6.3086C9.0223 6.70582 8.7072 7.1774 8.49222 7.69641C8.27724 8.21541 8.1666 8.77168 8.1666 9.33344C8.1666 9.89521 8.27724 10.4515 8.49222 10.9705C8.7072 11.4895 9.0223 11.9611 9.41953 12.3583C9.81676 12.7555 10.2883 13.0706 10.8073 13.2856C11.3263 13.5006 11.8826 13.6112 12.4444 13.6112C13.5789 13.6112 14.667 13.1605 15.4692 12.3583C16.2715 11.556 16.7222 10.468 16.7222 9.33344C16.7222 8.1989 16.2715 7.11083 15.4692 6.3086C14.667 5.50636 13.5789 5.05566 12.4444 5.05566ZM10.4999 9.33344C10.4999 8.81774 10.7048 8.32317 11.0694 7.95851C11.4341 7.59386 11.9287 7.389 12.4444 7.389C12.9601 7.389 13.4546 7.59386 13.8193 7.95851C14.184 8.32317 14.3888 8.81774 14.3888 9.33344C14.3888 9.84914 14.184 10.3437 13.8193 10.7084C13.4546 11.073 12.9601 11.2779 12.4444 11.2779C11.9287 11.2779 11.4341 11.073 11.0694 10.7084C10.7048 10.3437 10.4999 9.84914 10.4999 9.33344ZM12.4444 14.389C10.5839 14.389 8.84326 14.7623 7.52259 15.4234C6.27348 16.0488 5.05548 17.1283 5.05548 18.6668V18.7648C5.05237 19.5581 5.04926 20.8959 6.31548 21.8121C6.90348 22.2368 7.67971 22.509 8.64104 22.6817C9.61015 22.8559 10.853 22.9446 12.4444 22.9446C14.0357 22.9446 15.2786 22.8559 16.2477 22.6817C17.209 22.509 17.9853 22.2368 18.5733 21.8106C19.8395 20.8943 19.8364 19.5581 19.8333 18.7632V18.6668C19.8333 17.1268 18.6153 16.0488 17.3661 15.4234C16.0455 14.7639 14.3048 14.389 12.4444 14.389ZM7.38882 18.6668C7.38882 18.4879 7.56459 18.0119 8.56637 17.511C9.49971 17.0443 10.8686 16.7223 12.4444 16.7223C14.0202 16.7223 15.3906 17.0443 16.3224 17.511C17.3242 18.0119 17.4999 18.4879 17.4999 18.6668C17.4999 19.6063 17.4393 19.751 17.2044 19.9221C17.0146 20.059 16.6242 20.2426 15.8355 20.3857C15.0546 20.5257 13.9642 20.6112 12.4444 20.6112C10.9246 20.6112 9.83415 20.5257 9.05326 20.3857C8.2646 20.2426 7.87415 20.059 7.68437 19.9221C7.44793 19.751 7.38882 19.6063 7.38882 18.6668Z"
                        fill="currentColor"
                      />
                      <path
                        d="M28.0001 15.167C28.3095 15.167 28.6063 15.0441 28.8251 14.8253C29.0439 14.6065 29.1668 14.3097 29.1668 14.0003C29.1668 13.6909 29.0439 13.3942 28.8251 13.1754C28.6063 12.9566 28.3095 12.8337 28.0001 12.8337H21.7779C21.4685 12.8337 21.1717 12.9566 20.9529 13.1754C20.7341 13.3942 20.6112 13.6909 20.6112 14.0003C20.6112 14.3097 20.7341 14.6065 20.9529 14.8253C21.1717 15.0441 21.4685 15.167 21.7779 15.167H28.0001ZM29.1668 9.33366C29.1668 9.64308 29.0439 9.93982 28.8251 10.1586C28.6063 10.3774 28.3095 10.5003 28.0001 10.5003H20.2223C19.9129 10.5003 19.6162 10.3774 19.3974 10.1586C19.1786 9.93982 19.0557 9.64308 19.0557 9.33366C19.0557 9.02424 19.1786 8.72749 19.3974 8.5087C19.6162 8.28991 19.9129 8.16699 20.2223 8.16699H28.0001C28.3095 8.16699 28.6063 8.28991 28.8251 8.5087C29.0439 8.72749 29.1668 9.02424 29.1668 9.33366ZM28.0001 19.8337C28.3095 19.8337 28.6063 19.7107 28.8251 19.492C29.0439 19.2732 29.1668 18.9764 29.1668 18.667C29.1668 18.3576 29.0439 18.0608 28.8251 17.842C28.6063 17.6232 28.3095 17.5003 28.0001 17.5003H23.3334C23.024 17.5003 22.7273 17.6232 22.5085 17.842C22.2897 18.0608 22.1668 18.3576 22.1668 18.667C22.1668 18.9764 22.2897 19.2732 22.5085 19.492C22.7273 19.7107 23.024 19.8337 23.3334 19.8337H28.0001Z"
                        fill="currentColor"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.9131 0.388672H20.3095C23.1686 0.388672 25.4335 0.388672 27.2053 0.626672C29.0284 0.87245 30.5046 1.38889 31.6697 2.55245C32.8333 3.71756 33.3497 5.19378 33.5955 7.01689C33.8335 8.79023 33.8335 11.0536 33.8335 13.9127V14.0869C33.8335 16.946 33.8335 19.2109 33.5955 20.9827C33.3497 22.8058 32.8333 24.282 31.6697 25.4471C30.5046 26.6107 29.0284 27.1271 27.2053 27.3729C25.432 27.6109 23.1686 27.6109 20.3095 27.6109H13.9146C11.0555 27.6109 8.79062 27.6109 7.01885 27.3729C5.19574 27.1271 3.71951 26.6107 2.5544 25.4471C1.39085 24.282 0.874403 22.8058 0.628625 20.9827C0.390625 19.2093 0.390625 16.946 0.390625 14.0869V13.9127C0.390625 11.0536 0.390625 8.78867 0.628625 7.01689C0.874403 5.19378 1.39085 3.71756 2.5544 2.55245C3.71951 1.38889 5.19574 0.87245 7.01885 0.626672C8.79218 0.388672 11.054 0.388672 13.9131 0.388672ZM7.32685 2.93978C5.76196 3.14978 4.85974 3.54489 4.20018 4.20289C3.54374 4.86089 3.14862 5.76312 2.93862 7.328C2.72396 8.92712 2.72085 11.0333 2.72085 13.9998C2.72085 16.9662 2.72396 19.074 2.93862 20.6731C3.14862 22.2365 3.54374 23.1387 4.20174 23.7967C4.85974 24.4547 5.76196 24.8498 7.32685 25.0598C8.92596 25.2744 11.0322 25.2776 13.9986 25.2776H20.2208C23.1873 25.2776 25.2951 25.2744 26.8942 25.0598C28.4575 24.8498 29.3597 24.4547 30.0177 23.7967C30.6757 23.1387 31.0708 22.2364 31.2808 20.6716C31.4955 19.0724 31.4986 16.9662 31.4986 13.9998C31.4986 11.0333 31.4955 8.92712 31.2808 7.32645C31.0708 5.76312 30.6757 4.86089 30.0177 4.20289C29.3597 3.54489 28.4575 3.14978 26.8926 2.93978C25.2951 2.72512 23.1873 2.72201 20.2208 2.72201H13.9986C11.0322 2.72201 8.92751 2.72512 7.32685 2.93978Z"
                        fill="currentColor"
                      />{" "}
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-grey-800">
                    Role-Based Access Control
                  </h3>
                  <p>
                    Only authorized university personnel have access to
                    sensitive data, defined by strict permission protocols.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-5 col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="h-100 p-5 rounded-xl border border-grey-200 d-flex flex-column row-gap-3 bg-grey-50 text-center">
                <div className="rounded-md overflow-hidden">
                  <div className="flex-auto w-12 h-12 mx-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M26.5999 7.00039C26.5999 10.0944 20.9579 12.6004 13.9999 12.6004C7.0419 12.6004 1.3999 10.0944 1.3999 7.00039M26.5999 7.00039C26.5999 3.90639 20.9579 1.40039 13.9999 1.40039C7.0419 1.40039 1.3999 3.90639 1.3999 7.00039M26.5999 7.00039V14.0004M1.3999 7.00039V14.0004M1.3999 14.0004C1.3999 17.0944 7.0419 19.6004 13.9999 19.6004M1.3999 14.0004V21.0004C1.3999 24.0944 7.0419 26.6004 13.9999 26.6004M22.3999 18.2004V22.4004M22.3999 22.4004V26.6004M22.3999 22.4004H26.5999M22.3999 22.4004H18.1999"
                        stroke="#1E40AF"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-grey-800">
                    Data Retention & Deletion
                  </h3>
                  <p>
                    We enforce rigorous policies to retain and delete data in
                    accordance with institutional and regulatory requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="#"
              className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1"
            >
              Join PlutoEdu
            </a>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-xl">
          <div className="row align-items-center gx-xl-5">
            <div className="col-lg-5 col-md-6 col-sm-12 col-12">
              <img
                src="assets/images/home/about-your-personal-info.png"
                alt="Land Internships & Projects"
                className="img-fluid"
              />
            </div>
            <div className="col-lg-7 col-md-6 col-sm-12 col-12">
              <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-2xl font-bold">
                Seamless, Secure Integrations
              </h2>
              <p>
                Our API connections and third-party integrations are fortified
                with secure authentication and encryption protocols. This
                ensures that data exchanges between systems are both reliable
                and compliant with university security standards.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
